import React, { useCallback, useEffect, useState } from 'react'
import {
  Activity,
  Bot,
  CircleDollarSign,
  Gauge,
  LoaderCircle,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import CounselPracticeRegressionRunner from './CounselPracticeAsyncRegressionRunner'
import { api, getApiErrorMessage } from '../../lib/api'
import CounselPracticeQualitySnapshot from './CounselPracticeQualitySnapshot'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function compactNumber(value) {
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(Number(value) || 0)
}

function formatDate(value) {
  if (!value) return ''
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? value : `${date.getMonth() + 1}.${date.getDate()}`
}

function Metric({ icon: Icon, label, value, detail, tone = 'text-sky-700' }) {
  return (
    <div className="min-w-0 px-4 py-4 sm:px-5">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <Icon className={`h-4 w-4 ${tone}`} aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-950">{value}</div>
      {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
    </div>
  )
}

export default function CounselPracticeOperationsDashboard() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [quality, setQuality] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [analyticsResponse, qualityResponse] = await Promise.all([
        api.get('/counsel-practice/admin/analytics', {
          params: { days }, __silent: true,
        }),
        api.get('/counsel-practice/admin/quality', {
          params: { days }, __silent: true,
        }),
      ])
      setData(unwrap(analyticsResponse))
      setQuality(unwrap(qualityResponse))
    } catch (e) {
      setError(getApiErrorMessage(e, '운영 현황을 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="flex min-h-[28rem] items-center justify-center text-slate-500">
        <LoaderCircle className="h-6 w-6 animate-spin" aria-label="운영 현황 불러오는 중" />
      </div>
    )
  }

  const overview = data?.overview || {}
  const generation = data?.generation || {}
  const daily = Array.isArray(data?.daily) ? data.daily : []
  const contents = Array.isArray(data?.contents) ? data.contents : []
  const costs = Array.isArray(data?.costs) ? data.costs : []
  const generationFailures = Array.isArray(data?.generationFailures) ? data.generationFailures : []

  return (
    <section className="mt-5" aria-labelledby="counsel-operations-title" data-testid="counsel-operations-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <h2 id="counsel-operations-title" className="text-lg font-semibold text-slate-950">운영 현황</h2>
          <p className="mt-1 text-sm text-slate-600">연습 횟수와 응답 품질, 비용을 한곳에서 확인합니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(event) => setDays(Number(event.target.value))} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700" aria-label="조회 기간">
            <option value={7}>최근 7일</option>
            <option value={30}>최근 30일</option>
            <option value={90}>최근 90일</option>
          </select>
          <button type="button" onClick={load} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50" title="새로고침" aria-label="운영 현황 새로고침">
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}

      <div className="mt-5 grid overflow-hidden border border-slate-200 bg-white sm:grid-cols-2 xl:grid-cols-5">
        <Metric icon={Activity} label="연습 시작" value={`${compactNumber(overview.sessionCount)}회`} detail={`완료율 ${compactNumber(overview.completionRate)}%`} />
        <Metric icon={Gauge} label="평균 점수" value={`${compactNumber(overview.averageScore)}점`} />
        <Metric icon={Bot} label="AI 응답 성공률" value={`${compactNumber(generation.aiSuccessRate)}%`} detail={`대체 응답 ${compactNumber(generation.fallbackCount)}회`} />
        <Metric icon={Gauge} label="평균 응답 시간" value={`${compactNumber((generation.averageLatencyMs || 0) / 1000)}초`} detail={`최장 ${compactNumber((generation.maxLatencyMs || 0) / 1000)}초`} />
        <Metric icon={ShieldAlert} label="점검 필요" value={`${compactNumber((overview.safetyIssueCount || 0) + (overview.evaluationFailureCount || 0))}건`} detail={`분석 실패 ${compactNumber(overview.evaluationFailureCount)}건`} tone="text-amber-700" />
      </div>

      <CounselPracticeQualitySnapshot data={quality} />
      <CounselPracticeRegressionRunner />

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <section className="min-w-0 border-t border-slate-200 pt-5" aria-labelledby="daily-session-title">
          <h3 id="daily-session-title" className="text-base font-semibold text-slate-950">일별 연습 수</h3>
          <div className="mt-4 h-64 w-full" aria-label="일별 연습 수 그래프">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip labelFormatter={formatDate} formatter={(value) => [`${value}회`, '연습']} />
                <Bar dataKey="sessionCount" fill="#0284c7" radius={[3, 3, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="min-w-0 border-t border-slate-200 pt-5" aria-labelledby="daily-score-title">
          <h3 id="daily-score-title" className="text-base font-semibold text-slate-950">일별 평균 점수</h3>
          <div className="mt-4 h-64 w-full" aria-label="일별 평균 점수 그래프">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip labelFormatter={formatDate} formatter={(value) => [`${compactNumber(value)}점`, '평균 점수']} />
                <Line dataKey="averageScore" type="monotone" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3, fill: '#0f766e' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="generation-quality-title">
        <h3 id="generation-quality-title" className="text-base font-semibold text-slate-950">응답 품질</h3>
        <dl className="mt-4 grid border-y border-slate-200 sm:grid-cols-2 lg:grid-cols-5">
          {[
            ['가상 고객 응답', `${compactNumber(generation.responseCount)}회`],
            ['대체 응답', `${compactNumber(generation.fallbackRate)}%`],
            ['응답 실패', `${compactNumber(generation.failureCount)}회`],
            ['품질 기준 재생성', `${compactNumber(generation.repetitionRetryCount)}회`],
            ['AI 호출', `${compactNumber(generation.aiAttemptCount)}회`],
          ].map(([label, value]) => (
            <div key={label} className="px-4 py-4">
              <dt className="text-xs font-medium text-slate-500">{label}</dt>
              <dd className="mt-1 text-base font-semibold text-slate-900">{value}</dd>
            </div>
          ))}
        </dl>
        {generationFailures.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className="font-medium text-slate-700">실패 원인</span>
            {generationFailures.map((item) => (
              <span key={item.code} className="text-slate-600">
                {item.label || '기타 응답 오류'} <strong className="font-semibold text-slate-900">{compactNumber(item.count)}회</strong>
              </span>
            ))}
          </div>
        ) : null}
      </section>

      <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="counsel-cost-title">
        <div className="flex items-center gap-2">
          <CircleDollarSign className="h-5 w-5 text-emerald-700" aria-hidden="true" />
          <h3 id="counsel-cost-title" className="text-base font-semibold text-slate-950">AI 사용 비용</h3>
        </div>
        {costs.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">조회 기간에 기록된 AI 비용이 없습니다.</p>
        ) : (
          <div className="mt-4 overflow-x-auto border-y border-slate-200">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-4 py-3 font-medium">통화</th><th className="px-4 py-3 font-medium">호출</th><th className="px-4 py-3 font-medium">토큰</th><th className="px-4 py-3 font-medium">비용</th><th className="px-4 py-3 font-medium">단가 미설정</th></tr></thead>
              <tbody className="divide-y divide-slate-100">{costs.map((cost) => <tr key={cost.currency}><td className="px-4 py-3 font-medium text-slate-800">{cost.currency}</td><td className="px-4 py-3 text-slate-700">{compactNumber(cost.eventCount)}회</td><td className="px-4 py-3 text-slate-700">{compactNumber(cost.tokenCount)}</td><td className="px-4 py-3 font-semibold text-slate-900">{compactNumber(cost.amount)}</td><td className="px-4 py-3 text-slate-700">{compactNumber(cost.unpricedCount)}건</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="content-performance-title">
        <h3 id="content-performance-title" className="text-base font-semibold text-slate-950">콘텐츠별 결과</h3>
        <div className="mt-4 overflow-x-auto border-y border-slate-200">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-4 py-3 font-medium">연습 항목</th><th className="px-4 py-3 font-medium">유형</th><th className="px-4 py-3 font-medium">시작</th><th className="px-4 py-3 font-medium">완료율</th><th className="px-4 py-3 font-medium">평균 점수</th><th className="px-4 py-3 font-medium">준법 주의</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {contents.map((item) => <tr key={`${item.contentTypeLabel}-${item.contentTitle}`}><td className="max-w-[24rem] px-4 py-3 font-medium text-slate-900">{item.contentTitle}</td><td className="px-4 py-3 text-slate-600">{item.contentTypeLabel}</td><td className="px-4 py-3 text-slate-700">{compactNumber(item.sessionCount)}회</td><td className="px-4 py-3 text-slate-700">{compactNumber(item.completionRate)}%</td><td className="px-4 py-3 font-semibold text-slate-900">{compactNumber(item.averageScore)}점</td><td className="px-4 py-3 text-slate-700">{compactNumber(item.safetyIssueCount)}건</td></tr>)}
              {contents.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">조회 기간에 완료된 연습이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  )
}
